import React, { useEffect, useRef } from "react";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "components/modal/Modal";
import "./english-revert-modal.scss";

export interface EnglishRevertModalProps {
    onConfirm: () => void;
    onCancel: () => void;
}

export default function EnglishRevertModal({ onConfirm, onCancel }: EnglishRevertModalProps) {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!modalRef.current) {
                return;
            }

            if (event.key === "Escape") {
                onCancel();
                return;
            }

            if (event.key !== "Tab") {
                return;
            }

            const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );

            if (!focusableElements.length) {
                return;
            }

            const firstFocusableElement = focusableElements[0];
            const lastFocusableElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey && document.activeElement === firstFocusableElement) {
                event.preventDefault();
                lastFocusableElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
                event.preventDefault();
                firstFocusableElement.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onCancel]);

    useEffect(() => {
        const firstFocusableElement = modalRef.current?.querySelector<HTMLElement>("button");
        firstFocusableElement?.focus();
    }, []);

    const customHeader = (
        <div className="english-revert-modal__header-content">
            <div className="english-revert-modal__title" id="english-revert-modal-title">
                <span className="english-revert-modal__title--english GlobalLinkNoTx">
                    English Application Acknowledgement
                </span>
                <span className="english-revert-modal__title--spanish">
                    Acuse de recibo de solicitud
                </span>
            </div>
            <button
                type="button"
                className="english-revert-modal__close-btn"
                aria-label="Cerrar"
                onClick={onCancel}
            >
                <FontAwesomeIcon icon={regular("xmark")} />
            </button>
        </div>
    );

    const customBody = (
        <div className="english-revert-modal__body">
            <p className="english-revert-modal__question english-revert-modal__question--spanish" id="english-revert-modal-title">
                ¿Está seguro de que desea continuar en inglés?
            </p>
            <p className="english-revert-modal__question english-revert-modal__question--english GlobalLinkNoTx">
                Are you sure you want to continue<br className="english-revert-modal__mobile-break" /> in English?
            </p>
        </div>
    );

    const footer = (
        <div className="english-revert-modal__footer">
            <button
                type="button"
                className="english-revert-modal__cancel-cta"
                onClick={onCancel}
            >
                Cancelar
            </button>
            <button
                type="button"
                className="english-revert-modal__confirm-cta"
                onClick={onConfirm}
            >
                Continuar
            </button>
        </div>
    );

    return (
        <div
            className="english-revert-modal"
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="english-revert-modal-title"
        >
            <Modal
                customHeader={customHeader}
                customBody={customBody}
                footer={footer}
                footerContentPosition="right"
            />
        </div>
    );
}
